// از دادهٔ خامِ `admin_event_rank_stats` (مایگریشن 0157) تا ستون‌های سربرگِ
// «ترتیبِ پیشنهادی» در اپِ ادمین — WHOSUP-285.
//
// ═══ این فایل برای چیست ═══
//
// قرار است روزی رویدادها با یک فرمول مرتب شوند. پیش از ساختنِ فرمول، هر
// **جزء** جدا دیده می‌شود: توزیعش روی رویدادهای زنده، چند رویداد اصلاً
// مقدار دارند، و کدام عملاً همه‌جا یکی است و چیزی را از هم جدا نمی‌کند.
// این فایل هنوز هیچ امتیازِ ترکیبی نمی‌سازد — آن قدمِ بعد است.
//
// SQL فقط می‌شمارد؛ هر چیزی که «تصمیم» است — کدام فیلدها در «درصدِ پر بودن»
// حساب شوند، آدرسِ کامل یعنی چه، متنِ قیمت چطور عدد شود — این‌جاست تا با
// `npm test` آزموده شود و عوض کردنش مایگریشن نخواهد.

/** یک ردیفِ خام، همان‌طور که از RPC می‌آید. */
export type RankRow = {
  id: string;
  short_id: number | null;
  title: string;
  source: string;
  url: string | null;
  created_at: string | null;
  starts_at: string | null;
  ends_at: string | null;
  /** بازدیدِ خامِ صفحه‌ی جزئیات (هر بار باز شدن). */
  views: number;
  /** آدم‌های یکتایی که صفحه‌ی جزئیات را باز کردند. */
  viewers: number;
  clicks: number;
  clickers: number;
  /** نشان‌شده‌ها (`favorites`). */
  likes: number;
  /** سیگنال‌های پاک‌نشده‌ای که از روی این رویداد ساخته شدند. */
  plans: number;
  /** منبعِ اصلی + منابعِ مستقلی که همین را گزارش کردند. */
  sources: number;
  photos: number;
  image_ok: boolean;
  desc_len: number;
  address: string | null;
  venue_name: string | null;
  has_coords: boolean;
  is_free: boolean;
  price_note: string | null;
  has_ticket: boolean;
  /** ردیف‌های `event_occurrences`؛ صفر یعنی فقط همان یک اجرای روی خودِ ردیف. */
  occurrences: number;
  weekdays: number;
  monthdays: number;
  tags: number;
  /** رویدادهای زنده‌ی دیگر در همان مکان. */
  same_place: number;
  has_place: boolean;
  /** رویدادهای زنده‌ی دیگرِ همین منبع. */
  source_events: number;
};

export type RankStatsData = {
  generated_at: string;
  matched: number;
  shown: number;
  /** «چند ثانیه در صفحه‌اش ماندند» امروز هیچ‌جا ثبت نمی‌شود — مایگریشن 0157. */
  dwell_measured: boolean;
  rows: RankRow[];
};

/* ───────────────────────── جزءهای مشتق ───────────────────────── */

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * متنِ قیمت → کمترین عددی که در آن آمده (دلار).
 *
 * «رایگان» صفر است، چه از `is_free` و چه از متن. متنی که هیچ عددی ندارد
 * («See listing») `null` است و نه صفر — قیمتِ نامعلوم رایگان نیست.
 */
export function parsePrice(isFree: boolean, note: string | null): number | null {
  if (isFree) return 0;
  if (!note) return null;
  if (/\b(free|gratuit|gratuite)\b|رایگان/i.test(note)) return 0;
  const nums = (note.replace(/(\d),(\d{3})\b/g, '$1$2').match(/\d+(?:[.,]\d+)?/g) ?? [])
    .map((s) => Number(s.replace(',', '.')))
    .filter((n) => Number.isFinite(n));
  return nums.length ? Math.min(...nums) : null;
}

/** چند روز طول می‌کشد. بی‌تاریخ `null`، بی‌پایان یک روز. */
export function spanDays(startsAt: string | null, endsAt: string | null): number | null {
  if (!startsAt) return null;
  const s = Date.parse(startsAt);
  if (!Number.isFinite(s)) return null;
  const e = endsAt ? Date.parse(endsAt) : NaN;
  if (!Number.isFinite(e) || e <= s) return 1;
  return Math.max(1, Math.ceil((e - s) / DAY_MS));
}

/** چند اجرا دارد: ردیف‌های `event_occurrences`، و اگر نبود همان یکی. */
export const repeats = (r: Pick<RankRow, 'occurrences'>): number => Math.max(r.occurrences, 1);

/** قطعه‌های «آدرسِ کامل». هر کدام سهمِ برابر دارد. */
export const ADDRESS_PARTS = [
  { key: 'text', label: 'متنِ آدرس' },
  { key: 'number', label: 'پلاک' },
  { key: 'postal', label: 'کدپستی' },
  { key: 'coords', label: 'مختصات' },
  { key: 'venue', label: 'نامِ مکان' },
] as const;

export type AddressPart = (typeof ADDRESS_PARTS)[number]['key'];

/** کدپستیِ کانادا (K1A 0M8) یا زیپِ آمریکا (12345). */
const POSTAL = /\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b|\b\d{5}(?:-\d{4})?\b/i;
/** پلاکِ اولِ آدرس («1 Vimy Place»، «120-B Main St»). */
const HOUSE_NUMBER = /^\s*\d+[A-Z]?\b|\b\d+[A-Z]?\s+[A-Z][a-z]/;

export function addressParts(
  r: Pick<RankRow, 'address' | 'has_coords' | 'venue_name'>,
): Record<AddressPart, boolean> {
  const a = r.address?.trim() ?? '';
  return {
    text: a.length > 0,
    number: a.length > 0 && HOUSE_NUMBER.test(a),
    postal: a.length > 0 && POSTAL.test(a),
    coords: r.has_coords,
    venue: !!r.venue_name?.trim(),
  };
}

/** درصدِ کامل بودنِ آدرس، ۰ تا ۱۰۰. */
export function addressScore(r: Pick<RankRow, 'address' | 'has_coords' | 'venue_name'>): number {
  const p = addressParts(r);
  const n = ADDRESS_PARTS.filter((x) => p[x.key]).length;
  return Math.round((n / ADDRESS_PARTS.length) * 100);
}

/**
 * فیلدهایی که «درصدِ پر بودن» می‌شمارد.
 *
 * ⚠ لینکِ بلیت عمداً نیست: رویدادِ رایگان بلیت ندارد و نبودنش نقص نیست.
 * سنِ مجاز هم نیست، به همان دلیل.
 */
export const FIELDS: { key: string; label: string; has: (r: RankRow) => boolean }[] = [
  { key: 'title', label: 'عنوان', has: (r) => !!r.title?.trim() },
  { key: 'description', label: 'شرح', has: (r) => r.desc_len > 0 },
  { key: 'image', label: 'عکسِ سالم', has: (r) => r.image_ok },
  { key: 'starts_at', label: 'شروع', has: (r) => !!r.starts_at },
  { key: 'ends_at', label: 'پایان', has: (r) => !!r.ends_at },
  { key: 'place', label: 'مکان/آدرس', has: (r) => r.has_place || !!r.address?.trim() },
  { key: 'coords', label: 'مختصات', has: (r) => r.has_coords },
  { key: 'price', label: 'قیمت', has: (r) => r.is_free || !!r.price_note },
  { key: 'url', label: 'لینک', has: (r) => !!r.url?.trim() },
  { key: 'tags', label: 'تگ', has: (r) => r.tags > 0 },
];

/** درصدِ فیلدهای پر، ۰ تا ۱۰۰. */
export function filledPct(r: RankRow): number {
  const n = FIELDS.filter((f) => f.has(r)).length;
  return Math.round((n / FIELDS.length) * 100);
}

/* ───────────────────────── فهرستِ اجزا ───────────────────────── */

export type ComponentGroup = 'engagement' | 'content' | 'context';

export const GROUP_LABEL: Record<ComponentGroup, string> = {
  engagement: 'رفتارِ کاربران',
  content: 'کیفیتِ محتوا',
  context: 'زمینه',
};

export const GROUP_HELP: Record<ComponentGroup, string> = {
  engagement: 'آنچه کاربران با رویداد کرده‌اند: دیدن، لایک، ساختنِ پلن. هرچه بیشتر، یعنی رویداد واقعاً توجه گرفته.',
  content: 'خودِ اطلاعاتِ رویداد چقدر کامل و خوب است: عکس، شرح، قیمت، آدرس، فیلدهای پر.',
  context: 'چیزهایی بیرون از خودِ رویداد: چند منبع لیستش کرده‌اند، چند رویدادِ دیگر هم‌آدرس یا هم‌منبع‌اند، چند روز و چند بار برگزار می‌شود.',
};

export type RankComponent = {
  key: string;
  label: string;
  group: ComponentGroup;
  /** واحد برای نمایش — خالی یعنی شمارش. */
  unit?: '%' | '$' | 'روز' | 'نویسه';
  help: string;
  /** `null` یعنی برای این رویداد معلوم نیست (و نه صفر). */
  value: (r: RankRow) => number | null;
  /** امروز اصلاً اندازه گرفته نمی‌شود — ستون می‌ماند ولی خالی. */
  unmeasured?: boolean;
};

/** ترتیبِ اجزا همان ترتیبِ تیکت است. */
export const COMPONENTS: RankComponent[] = [
  { key: 'views', label: 'بازدید', group: 'engagement',
    help: 'چند بار صفحه‌ی جزئیاتش باز شده — هر بار، نه هر آدم (event_counters)',
    value: (r) => r.views },
  { key: 'viewers', label: 'بیننده', group: 'engagement',
    help: 'چند آدمِ واردشده دست‌کم یک بار جزئیاتش را باز کردند (event_viewers). مهمان شمرده نمی‌شود.',
    value: (r) => r.viewers },
  { key: 'likes', label: 'لایک', group: 'engagement',
    help: 'چند نفر نشانش کردند (favorites)',
    value: (r) => r.likes },
  { key: 'plans', label: 'پلن', group: 'engagement',
    help: 'چند سیگنالِ پاک‌نشده از روی همین رویداد ساخته شده',
    value: (r) => r.plans },
  { key: 'dwell', label: 'ثانیه در صفحه', group: 'engagement',
    help: 'امروز ثبت نمی‌شود: رویدادِ «صفحه» فقط الگوی مسیر را دارد و نه شناسه‌ی رویداد را. برای داشتنش اپ باید زمانِ ماندن در صفحه‌ی جزئیات را با شناسه‌ی رویداد بفرستد — کارِ جدا.',
    value: () => null, unmeasured: true },
  { key: 'sources', label: 'منبع', group: 'context',
    help: 'چند منبع همین رویداد را لیست کرده‌اند: منبعِ اصلی + تاییدهای منبعِ مستقل',
    value: (r) => r.sources },
  { key: 'photos', label: 'عکس', group: 'content',
    help: 'تعدادِ عکس‌ها: فهرستِ metadata.images اگر هست، وگرنه یک اگر image_url دارد',
    value: (r) => r.photos },
  { key: 'filled', label: 'پر بودنِ فیلدها', group: 'content', unit: '%',
    help: `درصدِ این ${FIELDS.length} فیلد که پرند: ${FIELDS.map((f) => f.label).join('، ')}`,
    value: filledPct },
  { key: 'same_place', label: 'هم‌آدرس', group: 'context',
    help: 'چند رویدادِ زنده‌ی دیگر در همان مکان (همان venue، وگرنه همان متنِ آدرس)',
    value: (r) => (r.has_place ? r.same_place : null) },
  { key: 'price', label: 'قیمت', group: 'content', unit: '$',
    help: 'کمترین عددِ متنِ قیمت؛ رایگان صفر. متنِ بی‌عدد («See listing») یعنی نامعلوم.',
    value: (r) => parsePrice(r.is_free, r.price_note) },
  { key: 'days', label: 'چند روزه', group: 'context', unit: 'روز',
    help: 'از شروع تا پایان، چند روز. بی‌پایان یعنی یک روز؛ بی‌تاریخ یعنی نامعلوم.',
    value: (r) => spanDays(r.starts_at, r.ends_at) },
  { key: 'repeats', label: 'تکرار', group: 'context',
    help: 'چند اجرا در event_occurrences دارد؛ بی‌ردیف یعنی یک. تکرارِ هفتگی/ماهانه بی‌ردیف در ستونِ «الگو» دیده می‌شود.',
    value: repeats },
  { key: 'desc_len', label: 'طولِ شرح', group: 'content', unit: 'نویسه',
    help: 'تعدادِ نویسه‌های شرح، بی فاصله‌ی دو سر',
    value: (r) => r.desc_len },
  { key: 'address', label: 'کاملیِ آدرس', group: 'content', unit: '%',
    help: `از ${ADDRESS_PARTS.length} قطعه چند تا هست: ${ADDRESS_PARTS.map((p) => p.label).join('، ')}`,
    value: addressScore },
  { key: 'source_events', label: 'رویدادِ دیگرِ منبع', group: 'context',
    help: 'منبعِ این رویداد چند رویدادِ زنده‌ی دیگر هم لیست کرده',
    value: (r) => r.source_events },
];

/* ───────────────────────── خلاصه‌ی هر جزء ───────────────────────── */

export type ComponentSummary = {
  key: string;
  /** چند رویداد مقدارِ معلوم دارند. */
  known: number;
  /** چند رویداد مقدارِ غیرصفر دارند. */
  nonZero: number;
  /** چند مقدارِ متفاوت — یک یعنی این جزء چیزی را از هم جدا نمی‌کند. */
  distinct: number;
  min: number | null;
  median: number | null;
  p90: number | null;
  max: number | null;
  mean: number | null;
};

/** صدکِ مرتب‌شده با نزدیک‌ترین رتبه — ساده و بی‌درون‌یابی. */
export function percentile(sorted: number[], p: number): number | null {
  if (!sorted.length) return null;
  const i = Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1));
  return sorted[i];
}

export function summarize(rows: RankRow[], c: RankComponent): ComponentSummary {
  const vals: number[] = [];
  for (const r of rows) {
    const v = c.value(r);
    if (v != null && Number.isFinite(v)) vals.push(v);
  }
  vals.sort((a, b) => a - b);
  const sum = vals.reduce((s, v) => s + v, 0);
  return {
    key: c.key,
    known: vals.length,
    nonZero: vals.filter((v) => v !== 0).length,
    distinct: new Set(vals).size,
    min: vals.length ? vals[0] : null,
    median: percentile(vals, 0.5),
    p90: percentile(vals, 0.9),
    max: vals.length ? vals[vals.length - 1] : null,
    mean: vals.length ? Math.round((sum / vals.length) * 10) / 10 : null,
  };
}

/**
 * ردیف‌ها مرتب بر اساسِ یک جزء. نامعلوم همیشه ته می‌نشیند، چه صعودی چه
 * نزولی — «قیمتِ نامعلوم» نه ارزان‌ترین است نه گران‌ترین.
 */
export function sortByComponent(rows: RankRow[], c: RankComponent, dir: 'asc' | 'desc'): RankRow[] {
  const k = dir === 'desc' ? -1 : 1;
  return rows
    .map((r) => ({ r, v: c.value(r) }))
    .sort((a, b) => {
      if (a.v == null && b.v == null) return 0;
      if (a.v == null) return 1;
      if (b.v == null) return -1;
      return (a.v - b.v) * k;
    })
    .map((x) => x.r);
}
